// External Packages
import { useState, useContext, useRef, useEffect, Fragment } from "react";
import { AiOutlinePlus } from "react-icons/ai";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { UtilityContext } from "../../context/util-context";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";

// Internal Components
import Heading from "../UI/Heading";
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import LoadingSpinner from "../UI/LoadingSpinner";
import ImageSelector from "../UI/ImageSelector";
import { MdRefresh } from "react-icons/md";

/** ========================================================================
 * * Add Vehicle Page
 * Renders a page, which allows adding a new vehicle to the database.
 * ========================================================================== */

export default function AddVehicle() {
  // Load Contexts
  const { navigate } = useContext(UtilityContext);
  const { locations } = useContext(DataContext);

  // Initialize Processing States
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [makes, setMakes] = useState([]);

  /* ======================= FETCH MAKES ======================= */
  const fetchMakes = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch-all", {
        table: "make",
      });
      setMakes(res);
      setLoading(false);
    } catch (err) {
      console.error(err);

      setLoading(false);
    }
  };

  // ==================================== VIN INPUT HANDLER
  /** This function is used to decode the VIN using POSH's API
   * and set the resulting vehicle information in the form */
  const vinInputHandler = async (vin) => {
    try {
      // Check if VIN is complete
      if (vin.length !== 17) return;
      setLoading(true);
      // Send request to decode VIN
      const response = await requestAdminAPI("/decode-vin", { vin: vin });
      // Extract Results
      const vehicleInfo = response.vehicleInfo;

      // Check if brand is added
      if (
        !makes.find(
          (make) => make.name.toLowerCase() === vehicleInfo.make.toLowerCase()
        )
      )
        throw new Error("Vehicle Make not found. Please add the make first.");

      // Set Results
      Object.keys(vehicleInfo).forEach((key) => {
        if (key === "vin") return;
        if (!document.getElementsByName(key)[0]) return;
        document.getElementsByName(key)[0].value = vehicleInfo[key];
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ==================================== SUBMIT HANDLER
  const submitHandler = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      const vin = event.target.vin.value;

      // Get images from the form
      const images = {
        cornerImage: event.target.cornerImage.files[0],
        frontImage: event.target.frontImage.files[0],
        backImage: event.target.backImage.files[0],
        sideImage: event.target.sideImage.files[0],
      };

      // Upload images and hold the URLs
      const imageUrls = {
        frontImage: images.frontImage
          ? await uploadImageToFirebase(
              `images/${vin}/front`,
              "front",
              images.frontImage,
              setUploading
            )
          : "",
        backImage: images.backImage
          ? await uploadImageToFirebase(
              `images/${vin}/back`,
              "back",
              images.backImage,
              setUploading
            )
          : "",
        sideImage: images.sideImage
          ? await uploadImageToFirebase(
              `images/${vin}/side`,
              "side",
              images.sideImage,
              setUploading
            )
          : "",
        cornerImage: images.cornerImage
          ? await uploadImageToFirebase(
              `images/${vin}/corner`,
              "corner",
              images.cornerImage,
              setUploading
            )
          : "",
      };

      // Create a payload for all vehicle information
      const payload = {
        id: event.target.vin.value,
        vin: event.target.vin.value,
        make: event.target.make.value,
        model: event.target.model.value,
        year: event.target.year.value,
        trim: event.target.trim.value,
        drive: event.target.drive.value,
        fuel: event.target.fuel.value,
        seats: event.target.seats.value,
        doors: event.target.doors.value,
        license: event.target.license.value.toUpperCase(),
        licenseState: event.target.licenseState.value.toUpperCase(),
        locationId: event.target.location.value,
        color: event.target.color.value,
        plan: event.target.plan.value,
        monthlyMiles: event.target.monthlyMiles.value,
        rate: event.target.rate.value,
        type: event.target.type.value?.toLowerCase(),
        description: event.target.description.value,
      };

      // Add image URLs to the payload
      Object.keys(imageUrls).forEach((key) => {
        if (imageUrls[key]) payload[key] = imageUrls[key];
      });

      // Add the vehicle to the database
      await requestAdminAPI("manage-vehicle", payload);

      // Navigate to vehicle info page
      navigate("/vehicles/register?id=" + payload.vin);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= GET DESCRIPTION FROM AI ======================= */
  const getAIResponse = async () => {
    try {
      const vehicle = {
        make: document.getElementById("make").value,
        model: document.getElementById("model").value,
        year: document.getElementById("year").value,
        trim: document.getElementById("trim").value,
        color: document.getElementById("color").value,
        plan: document.getElementById("plan").value,
      };
      if (
        !vehicle.make ||
        !vehicle.model ||
        !vehicle.year ||
        !vehicle.trim ||
        !vehicle.color ||
        !vehicle.plan
      )
        return;
      setLoading(true);
      const response = await requestAdminAPI("get-ai-response", {
        prompt: `Write a 50 word description for an online list of ${vehicle.year} ${vehicle.color} ${vehicle.make} ${vehicle.model} ${vehicle.trim}, which is included in the ${vehicle.plan} subscription plan.`,
      });
      document.getElementById("description").value = response;
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMakes();
  }, []);

  return (
    <div className="container grid">
      <Heading
        title="Add Vehicle"
        text="Vehicle ID is VIN. VIN, Make, Model and Year cannot be changed once the vehicle is added."
      />
      <form onSubmit={submitHandler} className="info">
        <DetailPair
          label="VIN"
          edit={true}
          inputOptions={{
            required: true,
            onChange: (e) => vinInputHandler(e.target.value),
            name: "vin",
          }}
        />
        <SelectPair
          label="Make"
          options={makes.map((make) => ({ id: make.id, name: make.name }))}
          edit={true}
          inputOptions={{ required: true, name: "make" }}
        />
        <DetailPair
          label="Model"
          edit={true}
          inputOptions={{ required: true, name: "model" }}
        />
        <DetailPair
          label="Year"
          edit={true}
          inputOptions={{ required: true, type: "number", name: "year" }}
        />
        <DetailPair
          label="Trim"
          edit={true}
          inputOptions={{ required: true, name: "trim" }}
        />
        <DetailPair
          label="License Plate"
          edit={true}
          inputOptions={{ required: true, name: "license" }}
        />
        <DetailPair
          label="License State"
          edit={true}
          inputOptions={{ required: true, name: "licenseState" }}
        />
        <SelectPair
          label="Home Location"
          edit={true}
          options={locations?.map((loc) => ({
            id: loc.id,
            name: loc?.name,
          }))}
          inputOptions={{ required: true, name: "location" }}
        />
        <SelectPair
          label="Drive"
          options={["AWD", "RWD", "FWD"]}
          edit={true}
          inputOptions={{ required: true, name: "drive" }}
        />
        <SelectPair
          label="Fuel Type"
          options={["Gasoline", "Hybrid", "Electric", "Plugin-Hybrid"]}
          edit={true}
          inputOptions={{ required: true, name: "fuel" }}
        />
        <SelectPair
          label="Body Type"
          options={[
            { name: "Sedan", id: "sedan" },
            { name: "SUV", id: "suv" },
            { name: "Coupe", id: "coupe" },
            { name: "Convertible", id: "convertible" },
            { name: "Hatchback", id: "hatchback" },
            { name: "Truck", id: "truck" },
            { name: "Crossover", id: "crossover" },
          ]}
          edit={true}
          inputOptions={{ required: true, name: "type" }}
        />
        <div className="grid grid-cols-2 gap-2">
          <DetailPair
            label="Seats"
            edit={true}
            inputOptions={{ required: true, type: "number", name: "seats" }}
          />
          <DetailPair
            label="Doors"
            edit={true}
            inputOptions={{ required: true, type: "number", name: "doors" }}
          />
        </div>
        <DetailPair
          label="Color"
          edit={true}
          inputOptions={{ required: true, name: "color" }}
        />
        <SelectPair
          label="Plan"
          edit={true}
          options={["LUXURY", "PLUSH", "POSH"]}
          inputOptions={{ required: true, name: "plan" }}
        />
        <DetailPair
          label="Monthly Miles"
          edit={true}
          inputOptions={{
            required: true,
            type: "number",
            name: "monthlyMiles",
          }}
        />
        <DetailPair
          label="Monthly Rate"
          edit={true}
          inputOptions={{ required: true, type: "number", name: "rate" }}
        />

        <ImageSelector
          classes="h-48 w-full"
          name="cornerImage"
          edit={true}
          label="CORNER IMAGE"
        />
        <ImageSelector
          classes="h-48 w-full"
          name="frontImage"
          edit={true}
          label="FRONT IMAGE"
        />
        <ImageSelector
          classes="h-48 w-full"
          name="backImage"
          edit={true}
          label="BACK IMAGE"
        />
        <ImageSelector
          classes="h-48 w-full"
          name="sideImage"
          edit={true}
          label="SIDE IMAGE"
        />
        <div className="md:col-span-4">
          <div className="flex items-center justify-between my-2 text-sm">
            <p className="text-stone-400">Description:</p>
            <div
              onClick={getAIResponse}
              className="flex items-center space-x-2 clickable"
            >
              <p className="text-sm text-stone-200">Generate Description</p>
              <MdRefresh className="text-xl text-stone-200" />
            </div>
          </div>
          <textarea
            className="w-full text-sm form-input"
            rows={6}
            id="description"
            name="description"
            placeholder="Fill in all information above before generating a description"
          />
        </div>

        {loading ? (
          <Fragment>
            <LoadingSpinner />
            {uploading && (
              <p className="text-sm text-stone-200">
                Uploading {uploading[0]}... {uploading[1]}%
              </p>
            )}
          </Fragment>
        ) : (
          <button type="submit" className="text-sm btn-primary">
            Submit
          </button>
        )}
      </form>
    </div>
  );
}
